import { RefObject } from "react";

/**
 * Objectのkeyとvalueを返却します。
 * {@link Object.entries} では戻り値の型に[string, any][]を指定しているため、引数で指定したObjectのkeyの型がstringになってしまうため、それを解決するために[keyof T, T[keyof T]][]を指定しています。
 * @param o
 * @returns
 */
export function entries<T extends Record<string, any>>(
  o: T
): [keyof T, T[keyof T]][] {
  return Object.entries(o);
}

/**
 * type predicate を使用して引数の型を推論します。
 *
 * この関数は、引数に指定した値が null または undefined ではなかった場合に引数の値が null および undefined 以外の型(T) として推論します。
 *
 * ```
 * function getValue(): string | null | undefined {
 *    let x: string | null | undefined;
 *    // (なにがしかの処理)
 *    return x
 * }
 *
 * const value = getValue();  // この時点では value は string | null | undefined として推論される
 * if (isNotNullish(value)) {
 *    value; // null または undefined ではないため string型 と推論される
 * } else {
 *    value; // null | undefined と推論される
 * }
 * ```
 * この関数は主に以下のように null と null 以外の値が混在するような配列に対し、filter や find 操作を行う際に使用することを想定しています。
 *
 * ```
 * function getArray(): (string | null | undefined)[] {
 *   return ["", null, undefined];
 * }
 * const a = getArray().filter(isNotNullish);  // a が string[] に推論される
 * const b = getArray().filter((x) => x != null);  // type predicate を使用しない場合、`x != null`  とfilter しているにもかかわらず b が (string | null | undefined)[] に推論される
 * ```
 *
 * 単純に値が null または undefined かどうかを検査したい場合は、`!isNotNullish`のような形でこの関数は使用せずに `value == null` の使用を検討してください。
 *
 * @param value 検査対象
 * @returns 引数が null または undefined ではない場合 true
 */
export function isNotNullish<T>(value: T | null | undefined): value is T {
  return value != null;
}

/**
 * 特定の要素が配列に含まれているかどうかを true または false で返します。
 * 標準の Array.prototype.includes を使用すると、そもそも Array に含まれない値を includes の
 * 引数に指定するとコンパイルエラーになる。
 *
 * ```
 * const arr = ["a", "b", "c"] as const;
 * arr.includes("d"); // <- コンパイルエラー
 * ```
 *
 * そのため、配列に含まれるかどうかのチェックを行いやすいよう unknown の配列として扱い、要素の検索を行います。
 * また、type predicate を使用して型推論を行います。
 *
 * @see https://developer.mozilla.org/ja/docs/Web/JavaScript/Reference/Global_Objects/Array/includes
 * @param arr 検査対象の配列
 * @param elm 検索する要素
 * @param fromIndex 検索し始める位置のゼロから始まるインデックス
 * @returns 含まれている場合、true
 */
export function includes<A extends ReadonlyArray<unknown>>(
  arr: A,
  elm: unknown,
  fromIndex: number = 0
): elm is A[number] {
  return arr.includes(elm, fromIndex);
}

interface ScrollToOption {
  behavior?: ScrollBehavior;
  offsetTop?: number;
}
/**
 * 指定した要素の位置にスクロールします。
 *
 * @param elm {@link RefObject<HTMLElement> 対象要素}
 * @param options ScrollToOption
 */
export function scrollTo<E extends HTMLElement>(
  elm: RefObject<E | null>,
  options?: ScrollToOption
) {
  window.requestAnimationFrame(() => {
    if (elm.current == null) {
      return;
    }
    const { behavior, offsetTop }: ScrollToOption = {
      behavior: "smooth",
      offsetTop: 0,
      ...options,
    };
    const { top } = elm.current.getBoundingClientRect();
    window.scrollTo({
      top: top + window.pageYOffset - offsetTop,
      behavior: behavior,
    });
  });
}
